import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Stack,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Refresh,
  Today,
  CalendarViewMonth,
  ViewAgenda,
  Add,
  NotificationsActive,
} from '@mui/icons-material';
import { PageHeader, ErrorState } from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import crmPlusService, { CalendarEvent, TASK_PRIORITIES, TaskPriority } from '../../services/crmPlusService';
import crmService, { ACTIVITY_TYPES, ActivityType, ActivityEntityType } from '../../services/crmService';
import { customerService } from '../../services/customerService';
import { salesService } from '../../services/salesService';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad = (n: number) => String(n).padStart(2, '0');

/** Local YYYY-MM-DD, avoiding the UTC shift of toISOString(). */
const ymd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const isSameDay = (a: Date, b: Date): boolean => ymd(a) === ymd(b);

const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, 0);

/** 6 weeks x 7 days, Sunday-first, spanning the leading/trailing days needed
 * to fill full weeks around the given month. */
const buildMonthGrid = (monthAnchor: Date): Date[][] => {
  const first = startOfMonth(monthAnchor);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());

  const weeks: Date[][] = [];
  const cursor = new Date(gridStart);
  for (let w = 0; w < 6; w++) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
};

const isTaskOverdue = (event: CalendarEvent): boolean =>
  event.source === 'task' &&
  event.status !== 'DONE' &&
  event.status !== 'CANCELLED' &&
  new Date(event.at).getTime() < Date.now();

const eventChipColor = (event: CalendarEvent): 'error' | 'primary' | 'info' => {
  if (isTaskOverdue(event)) return 'error';
  return event.source === 'task' ? 'primary' : 'info';
};

const fmtTime = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const fmtDayHeading = (d: Date): string =>
  d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const CrmCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const colors = useControlRoomColors();

  const today = useMemo(() => new Date(), []);
  const [monthAnchor, setMonthAnchor] = useState<Date>(startOfMonth(today));
  const [selectedDay, setSelectedDay] = useState<Date>(today);
  const [viewMode, setViewMode] = useState<'month' | 'agenda'>('month');

  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create dialog + reminder state
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [sendingReminder, setSendingReminder] = useState(false);

  const emptyForm = {
    kind: 'task' as 'task' | 'activity',
    title: '',
    priority: 'MEDIUM' as TaskPriority,
    activityType: 'CALL' as ActivityType,
    entityType: 'none' as ActivityEntityType | 'none',
    entityId: '',
    at: '', // YYYY-MM-DDTHH:mm
    followUpAt: '',
  };
  const [form, setForm] = useState(emptyForm);

  const localDateTime = (d: Date, hour = 9, minute = 0): string => {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hour, minute);
    return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
  };

  const [recordOptions, setRecordOptions] = useState<{ id: number; label: string }[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const loadRecordOptions = useCallback(async (type: ActivityEntityType | 'none') => {
    if (type === 'none') {
      setRecordOptions([]);
      return;
    }
    setRecordsLoading(true);
    try {
      let opts: { id: number; label: string }[] = [];
      if (type === 'lead') {
        const r = await crmService.getLeads({ limit: 200 });
        opts = (r.leads || []).map((l) => ({ id: l.id, label: l.company_name || `Lead #${l.id}` }));
      } else if (type === 'opportunity') {
        const rows: any = await salesService.getOpportunities();
        const list = Array.isArray(rows) ? rows : rows?.opportunities || [];
        opts = list.map((o: any) => ({
          id: o.id,
          label: `${o.opportunityName || o.opportunity_name || `Opp #${o.id}`}${o.customerName ? ` — ${o.customerName}` : ''}`,
        }));
      } else if (type === 'customer') {
        const rows: any = await customerService.getCustomers();
        const list = Array.isArray(rows) ? rows : rows?.customers || [];
        opts = list.map((c: any) => ({ id: c.id, label: c.company_name || c.companyName || c.name || `Customer #${c.id}` }));
      }
      setRecordOptions(opts);
    } catch {
      setRecordOptions([]);
    } finally {
      setRecordsLoading(false);
    }
  }, []);

  /** Change the linked-entity type: reset the chosen record and load the picker. */
  const setEntityType = (type: ActivityEntityType | 'none') => {
    setForm((f) => ({ ...f, entityType: type, entityId: '' }));
    loadRecordOptions(type);
  };

  const openCreate = (day?: Date) => {
    const base = day || selectedDay || today;
    setForm({ ...emptyForm, at: localDateTime(base) });
    setRecordOptions([]);
    setCreateOpen(true);
  };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const from = ymd(startOfMonth(monthAnchor));
    const to = ymd(endOfMonth(monthAnchor));
    crmPlusService
      .getTaskCalendar(from, to)
      .then((r) => setEvents(r.events))
      .catch((e: any) => setError(e?.message || 'Failed to load the calendar.'))
      .finally(() => setLoading(false));
  }, [monthAnchor]);

  useEffect(() => {
    load();
  }, [load]);

  const eventsByDay = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    (events || []).forEach((ev) => {
      const d = new Date(ev.at);
      if (isNaN(d.getTime())) return;
      const key = ymd(d);
      (map[key] ||= []).push(ev);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()));
    return map;
  }, [events]);

  const weeks = useMemo(() => buildMonthGrid(monthAnchor), [monthAnchor]);

  const selectedDayEvents = eventsByDay[ymd(selectedDay)] || [];

  const monthDaysWithEvents = useMemo(() => {
    const monthKey = `${monthAnchor.getFullYear()}-${pad(monthAnchor.getMonth() + 1)}`;
    return Object.keys(eventsByDay)
      .filter((k) => k.startsWith(monthKey))
      .sort()
      .map((k) => ({ key: k, date: new Date(`${k}T00:00:00`), items: eventsByDay[k] }));
  }, [eventsByDay, monthAnchor]);

  const goToday = () => {
    setMonthAnchor(startOfMonth(today));
    setSelectedDay(today);
  };
  const goPrevMonth = () => setMonthAnchor((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const goNextMonth = () => setMonthAnchor((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));

  const handleEventClick = (event: CalendarEvent, e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    if (event.link) navigate(event.link);
  };

  const handleCreate = async () => {
    const isTask = form.kind === 'task';
    if (!form.title.trim()) {
      setToast({ msg: isTask ? 'A title is required.' : 'A subject is required.', severity: 'error' });
      return;
    }
    if (!isTask && (form.entityType === 'none' || !form.entityId.trim())) {
      setToast({ msg: 'An activity must be linked to a lead, opportunity or customer (type + id).', severity: 'error' });
      return;
    }
    const iso = (v: string) => (v ? new Date(v).toISOString() : undefined);
    setSaving(true);
    try {
      if (isTask) {
        await crmPlusService.createTask({
          title: form.title.trim(),
          priority: form.priority,
          dueAt: iso(form.at),
          relatedEntityType: (form.entityType === 'none' ? 'none' : form.entityType) as any,
          relatedEntityId: form.entityId ? Number(form.entityId) : undefined,
        });
      } else {
        await crmService.createActivity({
          entityType: form.entityType as ActivityEntityType,
          entityId: Number(form.entityId),
          activityType: form.activityType,
          subject: form.title.trim(),
          occurredAt: iso(form.at),
          followUpAt: iso(form.followUpAt),
        });
      }
      setCreateOpen(false);
      setToast({ msg: `${isTask ? 'Task' : 'Activity'} added — it now appears on the calendar and in its section.`, severity: 'success' });
      load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Failed to save.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendReminder = async () => {
    setSendingReminder(true);
    try {
      const [st, res] = await Promise.all([crmPlusService.reminderStatus(), crmPlusService.reminderSendNow()]);
      const msg = res.sent
        ? st.smtpConfigured
          ? 'Reminder email sent to your inbox.'
          : 'Reminder generated — SMTP is not configured, so it was logged server-side, not delivered.'
        : 'Nothing needs a reminder right now.';
      setToast({ msg, severity: res.sent ? 'success' : 'info' });
    } catch (e: any) {
      setToast({ msg: e?.message || 'Failed to send reminder.', severity: 'error' });
    } finally {
      setSendingReminder(false);
    }
  };

  const EventChip: React.FC<{ event: CalendarEvent }> = ({ event }) => (
    <Tooltip title={`${fmtTime(event.at)} · ${event.kind}${event.link ? '' : ' (no link)'}`}>
      <Chip
        size="small"
        label={event.title}
        color={eventChipColor(event)}
        variant="outlined"
        onClick={(e) => handleEventClick(event, e)}
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          height: 20,
          fontSize: '0.6875rem',
          fontWeight: 600,
          cursor: event.link ? 'pointer' : 'default',
          '& .MuiChip-label': {
            px: 0.75,
            width: '100%',
            display: 'block',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
        }}
      />
    </Tooltip>
  );

  const AgendaRow: React.FC<{ event: CalendarEvent }> = ({ event }) => (
    <Paper
      key={String(event.id)}
      elevation={0}
      onClick={(e) => handleEventClick(event, e)}
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: `${eventChipColor(event)}.main`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: event.link ? 'pointer' : 'default',
        '&:hover': event.link ? { borderColor: 'primary.main' } : undefined,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 64, color: 'text.secondary' }}
      >
        {fmtTime(event.at)}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {event.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
          {event.source} · {event.kind}
        </Typography>
      </Box>
      {event.priority && (
        <Typography variant="caption" color="text.secondary">
          {event.priority}
        </Typography>
      )}
    </Paper>
  );

  /** Full-month list grouped by date — used both as the xs collapse and the
   * desktop "Agenda" view toggle. */
  const MonthAgendaList: React.FC = () =>
    monthDaysWithEvents.length === 0 ? (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 3, textAlign: 'center' }}>
        No events this month
      </Typography>
    ) : (
      <Stack spacing={2.5}>
        {monthDaysWithEvents.map(({ key, date, items }) => (
          <Box key={key}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {fmtDayHeading(date)}
            </Typography>
            <Stack spacing={1}>
              {items.map((ev) => (
                <AgendaRow key={String(ev.id)} event={ev} />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    );

  return (
    <Box>
      <PageHeader
        title="Calendar"
        subtitle="Tasks and activities scheduled across the CRM"
        actions={
          <>
            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              <ToggleButton value="month" aria-label="Month view">
                <CalendarViewMonth fontSize="small" />
              </ToggleButton>
              <ToggleButton value="agenda" aria-label="Agenda view">
                <ViewAgenda fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              size="small"
              variant="outlined"
              startIcon={<NotificationsActive />}
              onClick={handleSendReminder}
              disabled={sendingReminder}
            >
              Email my reminders
            </Button>
            <Button size="small" variant="contained" startIcon={<Add />} onClick={() => openCreate()}>
              Add
            </Button>
            <IconButton onClick={load} disabled={loading}>
              <Refresh />
            </IconButton>
          </>
        }
      />

      {error && !events ? (
        <ErrorState message={error} onRetry={load} />
      ) : loading && !events ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: viewMode === 'agenda' ? 12 : 8 }}>
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {monthAnchor.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" startIcon={<Today fontSize="small" />} onClick={goToday}>
                    Today
                  </Button>
                  <IconButton size="small" onClick={goPrevMonth} aria-label="Previous month">
                    <ChevronLeft />
                  </IconButton>
                  <IconButton size="small" onClick={goNextMonth} aria-label="Next month">
                    <ChevronRight />
                  </IconButton>
                </Stack>
              </Box>

              {viewMode === 'agenda' ? (
                <MonthAgendaList />
              ) : (
                <>
                  {/* Month grid — hidden on xs in favour of the agenda list below. */}
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5, mb: 0.5 }}>
                      {WEEKDAY_LABELS.map((label) => (
                        <Typography
                          key={label}
                          variant="caption"
                          sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'center', py: 0.5 }}
                        >
                          {label}
                        </Typography>
                      ))}
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5, gridAutoRows: '1fr' }}>
                      {weeks.flatMap((week) =>
                        week.map((day) => {
                          const inMonth = day.getMonth() === monthAnchor.getMonth();
                          const isToday = isSameDay(day, today);
                          const isSelected = isSameDay(day, selectedDay);
                          const dayEvents = eventsByDay[ymd(day)] || [];
                          const visible = dayEvents.slice(0, 3);
                          const extra = dayEvents.length - visible.length;
                          return (
                            <Box
                              key={ymd(day)}
                              onClick={() => setSelectedDay(day)}
                              sx={{
                                minHeight: 88,
                                minWidth: 0,
                                overflow: 'hidden',
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: isSelected ? 'primary.main' : colors.line,
                                backgroundColor: isSelected
                                  ? 'action.selected'
                                  : inMonth
                                  ? colors.panel
                                  : colors.panel2,
                                opacity: inMonth ? 1 : 0.55,
                                p: 0.75,
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.375,
                                transition: 'border-color .15s',
                                '&:hover': { borderColor: 'primary.main' },
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: isToday ? 800 : 600,
                                  color: isToday ? 'primary.main' : 'text.secondary',
                                  ...(isToday && {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }),
                                }}
                              >
                                {day.getDate()}
                              </Typography>
                              <Stack spacing={0.25} sx={{ overflow: 'hidden', minWidth: 0 }}>
                                {visible.map((ev) => (
                                  <EventChip key={String(ev.id)} event={ev} />
                                ))}
                                {extra > 0 && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.625rem', pl: 0.5, cursor: 'pointer' }}
                                  >
                                    +{extra} more
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Box>

                  {/* xs collapse: the month grid gives way to a scrollable agenda. */}
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <MonthAgendaList />
                  </Box>
                </>
              )}
            </Paper>
          </Grid>

          {viewMode === 'month' && (
            <Grid size={{ xs: 12, md: 4 }} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>
                  {isSameDay(selectedDay, today) ? 'Today' : selectedDay.toLocaleDateString('en-IN', { weekday: 'short' })}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  {fmtDayHeading(selectedDay)}
                </Typography>
                {selectedDayEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Nothing scheduled for this day.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {selectedDayEvents.map((ev) => (
                      <AgendaRow key={String(ev.id)} event={ev} />
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Create task / activity directly from the calendar */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add to calendar</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={form.kind}
              onChange={(_, v) => {
                if (!v) return;
                setForm((f) => ({ ...f, kind: v }));
                // An activity must be linked to a record — default to opportunity.
                if (v === 'activity' && form.entityType === 'none') setEntityType('opportunity');
              }}
            >
              <ToggleButton value="task">Task</ToggleButton>
              <ToggleButton value="activity">Activity</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              fullWidth
              required
              label={form.kind === 'task' ? 'Task title' : 'Subject'}
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />

            {form.kind === 'task' ? (
              <TextField
                select
                fullWidth
                label="Priority"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as TaskPriority }))}
              >
                {TASK_PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                select
                fullWidth
                label="Activity type"
                value={form.activityType}
                onChange={(e) => setForm((f) => ({ ...f, activityType: e.target.value as ActivityType }))}
              >
                {ACTIVITY_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                sx={{ minWidth: 150 }}
                label={form.kind === 'task' ? 'Link to (optional)' : 'Linked to'}
                value={form.entityType}
                onChange={(e) => setEntityType(e.target.value as ActivityEntityType | 'none')}
                required={form.kind === 'activity'}
              >
                {(form.kind === 'task' ? ['none', 'lead', 'opportunity', 'customer'] : ['lead', 'opportunity', 'customer']).map(
                  (t) => (
                    <MenuItem key={t} value={t}>
                      {t === 'none' ? 'Nothing' : t.charAt(0).toUpperCase() + t.slice(1)}
                    </MenuItem>
                  )
                )}
              </TextField>
              <TextField
                select
                fullWidth
                label={
                  form.entityType === 'none'
                    ? 'Record'
                    : `${form.entityType.charAt(0).toUpperCase() + form.entityType.slice(1)}`
                }
                value={form.entityId}
                onChange={(e) => setForm((f) => ({ ...f, entityId: e.target.value }))}
                disabled={form.entityType === 'none' || recordsLoading}
                required={form.kind === 'activity'}
                helperText={
                  recordsLoading
                    ? 'Loading…'
                    : form.entityType !== 'none' && recordOptions.length === 0
                    ? 'No records found'
                    : undefined
                }
              >
                {recordOptions.map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <TextField
              fullWidth
              label={form.kind === 'task' ? 'Due' : 'When'}
              type="datetime-local"
              value={form.at}
              onChange={(e) => setForm((f) => ({ ...f, at: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            {form.kind === 'activity' && (
              <TextField
                fullWidth
                label="Follow-up (optional)"
                type="datetime-local"
                value={form.followUpAt}
                onChange={(e) => setForm((f) => ({ ...f, followUpAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? 'Saving…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default CrmCalendarPage;
